/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Typed error model for HTTP and application-level errors.
 *
 * This file combines two concerns that belong together:
 *
 * 1. RAW Xcelerator CONTRACT TYPES — `ErrorSource`, `SiemensErrorObject`,
 *    `ErrorResponse`
 *    Mirror the backend error-response envelope exactly (1:1 with the shared
 *    backend interfaces). These types are used exclusively inside the
 *    `ErrorTransformerService` to parse the raw `HttpErrorResponse` body;
 *    no downstream consumer reads them directly.
 *
 * 2. NORMALISED APPLICATION MODEL — `NormalisedApiError`
 *    The single application-internal shape that all error consumers (services,
 *    components, form wrappers) work with after the `ErrorResponseInterceptor`
 *    has processed the response.
 *
 * 3. ERROR CLASS HIERARCHY — `AppError`, `ApiError`, `NetworkError`,
 *    `ValidationError`, `AuthError`
 *    Concrete classes with a discriminating `kind` literal that allow
 *    exhaustive type checks via `instanceof` or `switch (error.kind)`.
 *    Every `ApiError` (and its subclasses) carries a `normalisedError`
 *    property so callers have fully typed access to the Xcelerator fields
 *    without re-parsing the raw body.
 *
 * Key Features:
 * - Fully typed, aligned with Xcelerator / Siemens REST API Guidelines
 * - Backward-compatible: `NormalisedApiError` also represents legacy flat
 *   responses so the webapp stays functional during backend migration
 * - Immutable by design: all interface fields are `readonly`
 * - Multi-error support via `NormalisedApiError.all`
 * - Optional `correlationId` for end-to-end support traceability (T5)
 * - Optional `code` for stable, machine-readable error classification
 *
 * Related Modules:
 * - ErrorTransformerService   — maps `HttpErrorResponse` → `NormalisedApiError`
 * - ErrorResponseInterceptor  — calls the transformer for every HTTP error
 * - GlobalErrorHandlerService — consumes `AppError` subclasses for toast display
 *
 * Implementation Notes:
 * - `NormalisedApiError.all` always contains at least one entry (the primary
 *   error itself) so consumers can iterate without null-checks
 * - `SiemensErrorObject` and `ErrorResponse` are not exported for use
 *   outside the transformer — treat them as internal parsing types
 *
 * @see {@link https://developer.siemens.com/guidelines/api-guidelines/rest/error.html}
 *   Siemens Xcelerator REST API Guidelines v2.5.1 — Error Reporting
 */

import {HttpStatusCode} from '@angular/common/http';

// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Raw Xcelerator Contract Types (transformer-internal)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Source information for an error, identifying the erroneous field, parameter,
 * or request header. Mirrors the backend `ErrorSource` interface exactly.
 *
 * Per Siemens Xcelerator REST API Guidelines Rule [305].
 *
 * @see {@link https://www.rfc-editor.org/rfc/rfc6901} RFC 6901 — JSON Pointer
 */
export interface ErrorSource {
  /**
   * RFC 6901 JSON Pointer to the erroneous field in the request body.
   * Used by the transformer to populate `NormalisedApiError.source`.
   *
   * @example '/data/email'
   */
  readonly pointer?: string;
  /** Query parameter that caused the error. */
  readonly parameter?: string;
  /** Request header that caused the error. */
  readonly header?: string;
}

/**
 * Raw Xcelerator error entry as returned by the backend inside the `errors` array.
 * Mirrors the server-side `SiemensErrorObject` interface exactly (1:1).
 * Used only inside `ErrorTransformerService` — downstream code always works
 * with `NormalisedApiError` instead.
 *
 * Per Siemens Xcelerator REST API Guidelines Rule [305].
 *
 * Example JSON entry:
 * ```json
 * {
 *   "status": 404,
 *   "title": "Not Found",
 *   "code": "RESOURCE_NOT_FOUND",
 *   "detail": "User with identifier 'abc123' not found",
 *   "correlationId": "b3a1c2d4-e5f6-7890-abcd-ef1234567890"
 * }
 * ```
 */
export interface SiemensErrorObject {
  /** UUID uniquely identifying this specific occurrence of the error. */
  readonly id?: string;
  /**
   * Optional stable machine-readable error code, e.g. "RESOURCE_NOT_FOUND".
   * Present for BusinessException subclasses; absent for validation errors
   * (Xcelerator Priority 1 & 2).
   */
  readonly code?: string;
  /** HTTP status code repeated inside the error object, e.g. 400, 404, 500. */
  readonly status: number;
  /** Short, stable, human-readable summary. Must not change across occurrences of the same error type. */
  readonly title: string;
  /** Human-readable description specific to this occurrence of the error. */
  readonly detail: string;
  /** Source information identifying the erroneous field, parameter, or header. */
  readonly source?: ErrorSource;
  /** Links to further information about this error type. */
  readonly links?: {readonly about?: string};
  /**
   * UUID for end-to-end request tracing. Echoed from or assigned to the
   * `X-Correlation-Id` header. Absent on legacy responses that predate Xcelerator.
   */
  readonly correlationId?: string;
}

/**
 * Xcelerator HTTP error response envelope as returned by the backend.
 * Mirrors the server-side `ErrorResponse` interface exactly (1:1).
 * The `errors` array MUST NOT be empty per Siemens REST API Guidelines Rule [304].
 *
 * Example JSON body:
 * ```json
 * {
 *   "errors": [
 *     {
 *       "status": 400,
 *       "title": "Validation Error",
 *       "detail": "email must be an email",
 *       "correlationId": "b3a1c2d4-e5f6-7890-abcd-ef1234567890"
 *     }
 *   ]
 * }
 * ```
 */
export interface ErrorResponse {
  /** Non-empty array of error objects conforming to the Siemens REST API Guidelines. */
  readonly errors: readonly SiemensErrorObject[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — Normalised Application Model
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Application-internal normalized representation of an HTTP API error.
 *
 * This is the single typed shape that all error consumers in the webapp work
 * with. It is produced by the `ErrorResponseInterceptor` via the
 * `ErrorTransformerService` for every HTTP error response, regardless of
 * whether the backend has already migrated to Xcelerator or still returns the
 * legacy flat error shape.
 *
 * Usage example — subscribing to an API call:
 * ```TypeScript
 * this.apiClient.get<User>('/users/123').subscribe({
 *   error: (err: ApiError) => {
 *     console.error('correlationId:', err.normalisedError.correlationId);
 *     this.toastService.show({ message: err.normalisedError.detail, type: 'error' });
 *   },
 * });
 * ```
 *
 * Multi-error usage — iterating all validation errors:
 * ```TypeScript
 * error.normalisedError.all.forEach(entry => console.warn(entry.detail));
 * ```
 */
export interface NormalisedApiError {
  /** HTTP status code of the response, e.g. 400, 404, 500. */
  readonly status: number;

  /**
   * Short, stable summary of the error category.
   * Sourced from `SiemensErrorObject.title` for Xcelerator responses, or
   * synthesized from the HTTP status text for legacy responses.
   */
  readonly title: string;

  /**
   * Occurrence-specific human-readable description of what went wrong.
   * Sourced from `errors[0].detail` for Xcelerator responses, or from the
   * legacy `error.message` field as a fallback.
   * Use this field for toast messages and user-facing error display.
   */
  readonly detail: string;

  /**
   * Optional UUID for end-to-end request tracing through backend logs.
   * Sourced from `errors[0].correlationId`. Absent on legacy responses.
   * Log this value to the browser console when present (TDR-007 T5 — Option B).
   */
  readonly correlationId?: string;

  /**
   * Optional stable machine-readable error code, e.g. "RESOURCE_NOT_FOUND".
   * Present for BusinessException subclasses (NestJS HttpException path).
   * Absent for validation errors (Xcelerator Priority 1 & 2).
   */
  readonly code?: string;

  /**
   * Optional source information identifying the erroneous field, parameter, or
   * header. Sourced from `SiemensErrorObject.source`. Present only when the
   * backend includes field-level location data (e.g. RFC 6901 pointer).
   * Reserved for future use when field-level highlighting is implemented.
   */
  readonly source?: ErrorSource;

  /**
   * All error objects from the original response, including the primary one.
   * Contains exactly one entry for single-error responses (most 4xx / 5xx).
   * Contains multiple entries for validation responses (Xcelerator Priority 1 & 2).
   * Consumers can iterate this array to display all field-level error details.
   *
   * Note: this array is always non-empty — the primary error is always included.
   */
  readonly all: readonly NormalisedApiError[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Error Class Hierarchy
// ─────────────────────────────────────────────────────────────────────────────

/** Common base class for all application-level errors. */
export abstract class AppError extends Error {
  abstract readonly kind: string;
}

/**
 * Generic HTTP error for 4xx and 5xx responses.
 *
 * Carries a `normalisedError` property so that all downstream consumers have
 * fully typed access to the Xcelerator fields (`detail`, `correlationId`, `all`,
 * etc.) without reparsing the raw body.
 *
 * @param status          - The HTTP status code returned by the server.
 * @param serverMessage   - The human-readable message extracted from the
 *                          response body (used as `Error.message`).
 * @param body            - The raw response body for further inspection.
 * @param normalisedError - The typed, normalized representation of the error.
 */
export class ApiError extends AppError {
  // Declared with a string base type so subclasses can narrow it further.
  declare readonly kind: string;

  constructor(
    public readonly status: number,
    public readonly serverMessage: string,
    public readonly body: unknown,
    public readonly normalisedError: NormalisedApiError,
  ) {
    super(`HTTP ${status}: ${serverMessage}`);
    this.name = 'ApiError';
    (this as {kind: string}).kind = 'ApiError';
  }
}

/**
 * Network-level error when the request never reached the server (status === 0).
 * Typically caused by the client being offline or a CORS preflight failure.
 *
 * @param networkCause - The underlying error or ProgressEvent from the browser.
 */
export class NetworkError extends AppError {
  override readonly kind = 'NetworkError' as const;

  constructor(public readonly networkCause: unknown) {
    super('Network unreachable');
    this.name = 'NetworkError';
  }
}

/**
 * HTTP 400 validation error with structured field-level details from the server.
 *
 * Carries the full `normalisedError.all` array so that form wrappers can
 * iterate every field error individually without reparsing the raw body.
 *
 * @param normalisedError - The typed, normalized representation containing all
 *                          field-level validation errors in the `all` array.
 * @param body            - The raw response body kept for API consistency.
 */
export class ValidationError extends ApiError {
  override readonly kind = 'ValidationError' as const;

  constructor(normalisedError: NormalisedApiError, body: unknown) {
    super(
      HttpStatusCode.BadRequest,
      'Validation failed',
      body,
      normalisedError,
    );
    this.name = 'ValidationError';
  }
}

/**
 * HTTP 401 or 403 error indicating missing or insufficient authentication.
 *
 * The `GlobalErrorHandlerService` redirects to the login page for these errors
 * instead of showing a toast notification.
 *
 * @param status          - Either `HttpStatusCode.Unauthorized` (401) or `HttpStatusCode.Forbidden` (403).
 * @param serverMessage   - The human-readable message from the response body.
 * @param body            - The raw response body.
 * @param normalisedError - The typed, normalized representation of the error.
 */
export class AuthError extends ApiError {
  override readonly kind = 'AuthError' as const;

  constructor(
    status: HttpStatusCode.Unauthorized | HttpStatusCode.Forbidden,
    serverMessage: string,
    body: unknown,
    normalisedError: NormalisedApiError,
  ) {
    super(status, serverMessage, body, normalisedError);
    this.name = 'AuthError';
  }
}

/** Discriminated union of all concrete application error types. */
export type AppErrorUnion =
  | ApiError
  | NetworkError
  | ValidationError
  | AuthError;
